/**
 * Contenido de ayuda contextual para cada panel del Care Manager Dashboard.
 * Cada entrada tiene: title, description, y sections (array de bloques explicativos).
 */

export const CM_HELP_CONTENT = {
  // ─── Location Selector ────────────────────────────────────────
  locationSelector: {
    title: "Location Selector",
    description:
      "Select a clinical location to load its Care Management data. All panels update automatically when you change locations.",
    sections: [
      {
        heading: "How it works",
        items: [
          "Choose from 11 available clinical locations across Florida",
          "The number next to each location shows total assigned patients",
          "Your last selection is remembered for your next visit",
          "All 7 dashboard panels refresh with the selected location's data",
        ],
      },
    ],
  },

  // ─── Panel 1: CM Overview ─────────────────────────────────────
  cmOverview: {
    title: "Care Management Overview",
    description:
      "High-level KPIs summarizing your Care Management workload. Click any card to jump to the relevant panel below.",
    sections: [
      {
        heading: "KPI Cards",
        items: [
          "In Active CM (blue) — Patients currently enrolled in Care Management programs (referred, engaged, or attempted contact)",
          "Unmanaged High Risk (red/green) — High-risk patients (At Risk, Complex, End of Life) not yet enrolled in CM. Red if count > 0, green if fully managed",
          "Post-Discharge Pending (amber/green) — Recently discharged patients awaiting follow-up. Amber if any pending, green if all followed up",
          "ER Frequent Flyers (red/green) — Patients with 3+ ER visits. Red if any exist, green if none",
        ],
      },
      {
        heading: "Clicking a card",
        items: [
          "Unmanaged High Risk → scrolls to Panel 3 (Unmanaged Risk)",
          "Post-Discharge Pending → scrolls to Panel 2 (Transitions)",
          "ER Frequent Flyers → scrolls to Panel 4 (ER & Utilization)",
        ],
      },
    ],
  },

  // ─── Panel 2: Transitions ─────────────────────────────────────
  transitions: {
    title: "Transition of Care",
    description:
      "Recent hospital discharges requiring follow-up. Sorted by LACE+ score (readmission risk) to prioritize the highest-risk patients.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "Avg LACE+ — Average LACE readmission risk score across all discharges",
          "High LACE — Count of discharges with LACE score > 15 (high readmission risk)",
          "Expired — Patients who passed away during admission",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Discharged patient name",
          "Facility — Hospital where the admission occurred",
          "Admit/Discharge Date — Admission and discharge dates",
          "LOS — Length of Stay in days. Red chip if 7+ days",
          "LACE+ — Readmission risk score: Green (0-9 Low), Amber (10-15 Moderate), Red (16+ High)",
          "BH — Brain icon indicates behavioral health diagnosis",
          "Disposition — Discharge outcome. Red text if 'Expired'",
          "Follow-up Window — Days since discharge. Pulsing red if ≤2 days (urgent), amber if 3-7 days",
          "Action — 'Follow-up' creates a pre-filled task for this patient",
        ],
      },
      {
        heading: "Show Expired Toggle",
        items: [
          "By default, deceased patients are hidden from the table",
          "Check 'Show Expired' to include them in the list",
        ],
      },
    ],
  },

  // ─── Panel 3: Unmanaged Risk ──────────────────────────────────
  unmanagedRisk: {
    title: "Unmanaged High Risk",
    description:
      "Patients classified as At Risk, Complex, or End of Life who are NOT currently enrolled in a Care Management program. These patients need to be assessed and potentially enrolled.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "At Risk (orange) — Patients with elevated risk factors not in CM",
          "Complex (red) — Multi-morbidity patients not in CM",
          "End of Life (purple) — Palliative/hospice-eligible patients not in CM",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Patient name",
          "Status — Health status: End of Life (purple), Complex (red), At Risk (orange)",
          "Global Indicator — Risk score from 1-7 (higher = more complex)",
          "Conditions — Primary diagnoses (hover for full list)",
          "Gaps — Open care gap count",
          "ER — Emergency room visit count",
          "Meds — Medication count",
          "Triggers — Active clinical trigger count",
          "Last Visit — Time since last appointment",
          "Action — 'Add to Worklist' creates a care plan task",
        ],
      },
      {
        heading: "Sort Order",
        items: [
          "End of Life patients appear first, then Complex, then At Risk",
          "Within each group, sorted by Global Indicator (highest first)",
        ],
      },
    ],
  },

  // ─── Panel 4: ER & Utilization ────────────────────────────────
  erUtilization: {
    title: "ER & Utilization",
    description:
      "Patients with frequent emergency room visits. Identifies preventable ER utilization patterns for intervention.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "ER Frequent Flyers (red) — Patients with 3+ ER visits in the reporting period",
          "Preventable ER (amber) — Total ER visits flagged as potentially preventable",
          "BH-Related ER (indigo) — ER visits related to behavioral health conditions",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Patient name",
          "ER Events — Total ER visit count (red badge)",
          "Preventable — Count of preventable ER visits",
          "PPV-ER — Predictive model score: LOW (green), MEDIUM (amber), HIGH (red)",
          "Status — Health status chip",
          "Conditions — Primary diagnoses",
          "BH — Brain icon if behavioral health related",
          "Last ER Date — Most recent ER visit date",
          "Action — 'Assign Intervention' creates an outreach task",
        ],
      },
    ],
  },

  // ─── Panel 5: Outreach ────────────────────────────────────────
  outreach: {
    title: "Outreach & Referrals",
    description:
      "Patients who need to be contacted for appointment scheduling. Sorted by health severity — the most critical patients appear first.",
    sections: [
      {
        heading: "Stats Cards",
        items: [
          "Patients to Contact (green) — Total patients needing outreach",
          "With Phone (blue) — Patients with phone number on file",
          "With Email (cyan) — Patients with email address on file",
        ],
      },
      {
        heading: "Filter Chips",
        items: [
          "'All' — Show all outreach patients",
          "'With Phone Only' — Show only patients with phone numbers",
          "'With Email Only' — Show only patients with email addresses",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Patient name",
          "Time Since Visit — Last visit timeframe, color-coded by urgency",
          "Phone — Clickable phone link (opens dialer)",
          "Mobile — Mobile phone number",
          "Email — Clickable email link (opens email client)",
          "Status — Health status chip",
          "Action — 'Log Call' creates a patient recall task",
        ],
      },
    ],
  },

  // ─── Panel 6: Tasks ───────────────────────────────────────────
  cmTasks: {
    title: "My Tasks",
    description:
      "Personal task manager for Care Management follow-ups. Tasks are saved locally and persist between sessions. Tasks can be created from any panel's action buttons.",
    sections: [
      {
        heading: "Stats",
        items: [
          "Due Today (amber) — Tasks due for completion today",
          "Overdue (red) — Tasks past their due date",
          "Done Today (green) — Tasks completed today",
          "This Week — Tasks created in the last 7 days",
        ],
      },
      {
        heading: "Task Groups",
        items: [
          "Overdue — Red background, immediate attention needed",
          "Due Today — Tasks scheduled for today",
          "Upcoming — Future tasks",
          "Completed — Recently finished tasks (last 5 shown)",
        ],
      },
      {
        heading: "Creating Tasks",
        items: [
          "'+ New Task' opens the task creation form",
          "Tasks are also created from: Transitions ('Follow-up'), Unmanaged Risk ('Add to Worklist'), ER ('Assign Intervention'), Outreach ('Log Call'), Polypharmacy ('Schedule Reconciliation')",
          "9 categories: Follow-Up, Care Gap, Coding Review, Outreach Call, Lab Review, Referral, Documentation, Patient Recall, Other",
          "3 priority levels: High, Medium, Low",
        ],
      },
    ],
  },

  // ─── Panel 7: Polypharmacy ────────────────────────────────────
  polypharmacy: {
    title: "Polypharmacy Alert",
    description:
      "Patients taking 5 or more medications who may need medication reconciliation. High medication counts increase risk of drug interactions and adverse events.",
    sections: [
      {
        heading: "Stats",
        items: [
          "Total count of patients on 5+ medications",
          "On 8+ Meds (red) — Patients on 8 or more medications (high risk for interactions)",
          "Without Recent Labs (amber) — Patients lacking recent lab work to monitor medication effects",
        ],
      },
      {
        heading: "Table Columns",
        items: [
          "Patient — Patient name",
          "Meds — Medication count. Amber (5-7), Red (8+)",
          "Status — Health status chip",
          "Conditions — Primary diagnoses",
          "Labs — Green check if recent labs available, amber warning if not",
          "Last Visit — Time since last appointment",
          "Global Indicator — Risk score",
          "Action — 'Schedule Reconciliation' creates a clinical task for medication review",
        ],
      },
    ],
  },
};
